import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Botao } from '../../componentes/base';
import { AreaSensivel, CampoData } from '../../componentes/formulario';
import { Modal } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { mensagemDeErro, post } from '../../nucleo/api';
import { dinheiro } from '../../nucleo/formato';
import { hojeLocal, invalidarContas, type DocumentoDetalhe } from './tipos';

/**
 * Fase 2 (W14): estorno da perda (`POST /contas/documentos/:id/estornar-perda`, permissão `contas.perder`). O saldo
 * volta a ser cobrado e a despesa de perdas com créditos é estornada na data escolhida; o mês da perda não muda.
 */
export function EstornarPerda({ doc, aoFechar }: { doc: DocumentoDetalhe | null; aoFechar: () => void }) {
  if (!doc) return null;
  return <Corpo doc={doc} aoFechar={aoFechar} />;
}

function Corpo({ doc, aoFechar }: { doc: DocumentoDetalhe; aoFechar: () => void }) {
  const { t } = useTranslation('contas');
  const { t: tn } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const [chave] = useState(() => crypto.randomUUID());
  const [data, setData] = useState(hojeLocal());
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const perda = doc.baixas.find((b) => b.tipo === 'perda' && !b.estornada_em);
  const estornar = useMutation({
    mutationFn: () => post(`/contas/documentos/${doc.id}/estornar-perda`, { data, motivo: motivo.trim() || undefined }, { 'Idempotency-Key': chave }),
    onSuccess: async () => {
      await invalidarContas(qc);
      toast.sucesso(t('w14.perdaEstornada'));
      aoFechar();
    },
    onError: (e) => setErro(mensagemDeErro(e)),
  });
  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      titulo={t('w14.estornoTitulo', { numero: doc.numero })}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.voltar')}
          </Botao>
          <Botao variante="primario" disabled={!/^\d{4}-\d{2}-\d{2}$/.test(data)} carregando={estornar.isPending} onClick={() => (setErro(null), estornar.mutate())}>
            {t('w14.estornarPerda')}
          </Botao>
        </>
      }
    >
      <p className="mb-3 text-sm text-tinta-2" data-estornar-perda>
        {t('w14.estornoTexto', { saldo: dinheiro(perda?.valor_centavos ?? 0, doc.moeda) })}
      </p>
      <div className="flex flex-col gap-3">
        <CampoData type="date" rotulo={t('w14.estornoData')} value={data} onChange={(e) => setData(e.target.value)} />
        <label className="flex flex-col gap-1.5 text-sm font-medium text-tinta-2">
          {t('w14.estornoMotivo')}
          <AreaSensivel rows={2} maxLength={300} value={motivo} onChange={(e) => setMotivo(e.target.value)} className="w-full rounded-lg border border-borda-campo bg-campo px-3 py-2 text-sm text-tinta-1 focus:border-foco focus:outline-2 focus:outline-offset-0 focus:outline-foco" />
        </label>
        {erro && (
          <p role="alert" className="rounded-lg border border-perigo px-3 py-2 text-sm text-perigo">
            {erro}
          </p>
        )}
      </div>
    </Modal>
  );
}
